import { useState } from 'react';
import { Eye, Code, X, Copy, Check, ShieldCheck } from 'lucide-react';
import { useAdminUsers, useAdminStrategiesByUser, useStrategy } from '../../api/hooks';
import { useSessionStore } from '../../session/store';
import { SectionHeader, EmptyState, ErrorState, Spinner } from '../../components/ui';
import { fmtDateTime, cn } from '../../lib/utils';
import { toast } from '../../store';

// ─── Schema version badge ──────────────────────────────────────────────────────
function SchemaBadge({ version }) {
  const isV2 = version === '2.0' || version === 2;
  return (
    <span className={cn(
      'badge border text-2xs font-mono',
      isV2
        ? 'bg-amber/10 text-amber border-amber/20'
        : 'bg-bg-hover text-txt-muted border-bg-border'
    )}>
      {isV2 ? 'v2.0' : 'v1'}
    </span>
  );
}

// ─── Strategy status badge ────────────────────────────────────────────────────
function StatusChip({ status }) {
  const s = (status || 'unknown').toLowerCase();
  const color =
    s === 'active'   ? 'text-bull' :
    s === 'paused'   ? 'text-warn' :
    s === 'stopped'  ? 'text-bear' :
    s === 'error'    ? 'text-bear' :
    s === 'ready'    ? 'text-info' :
    'text-txt-muted';
  return <span className={cn('font-mono text-xs capitalize', color)}>{status || '—'}</span>;
}

// ─── Details Drawer ───────────────────────────────────────────────────────────
function StrategyDetailsDrawer({ strategy, user, open, onClose }) {
  if (!open || !strategy) return null;

  const rows = [
      { label: 'Strategy ID',    value: <span className="font-mono text-txt-secondary">{strategy.id}</span> },
    { label: 'Owner',          value: <span className="font-semibold text-txt-primary">{user?.full_name || user?.username || strategy.user_id || '—'}</span> },
    { label: 'Schema Version', value: <SchemaBadge version={strategy.schema_version} /> },
    { label: 'Status',         value: <StatusChip status={strategy.status} /> },
    { label: 'Description',    value: <span className="text-txt-secondary">{strategy.description || <span className="text-txt-muted italic">none</span>}</span> },
    { label: 'Created At',     value: <span className="font-mono text-txt-secondary">{fmtDateTime(strategy.created_at)}</span> },
    { label: 'Updated At',     value: <span className="font-mono text-txt-secondary">{fmtDateTime(strategy.updated_at)}</span> },
  ];

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md bg-bg-panel border-l border-bg-border flex flex-col animate-slide-in shadow-modal">
        <div className="flex items-center justify-between px-5 py-4 border-b border-bg-border shrink-0">
          <div>
            <p className="text-2xs font-mono text-txt-muted uppercase tracking-widest mb-0.5">Strategy Details</p>
            <h3 className="font-display font-semibold text-sm text-txt-primary leading-tight">{strategy.name}</h3>
          </div>
          <button onClick={onClose} className="btn-ghost p-1.5 rounded-lg"><X size={15} /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          {rows.map(({ label, value }) => (
            <div key={label} className="flex flex-col gap-0.5">
              <span className="text-2xs font-mono text-txt-muted uppercase tracking-widest">{label}</span>
              <div className="text-xs">{value}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── JSON Drawer ──────────────────────────────────────────────────────────────
function StrategyJsonDrawer({ strategyId, open, onClose }) {
  const [copied, setCopied] = useState(false);
  const { data, isLoading, isError } = useStrategy(strategyId);

  if (!open) return null;

  const jsonPayload = data?.config ?? null;
  const jsonText = jsonPayload != null
    ? JSON.stringify(jsonPayload, null, 2)
    : data != null
      ? JSON.stringify(data, null, 2)
      : null;

  const handleCopy = async () => {
    if (!jsonText) return;
    try {
      await navigator.clipboard.writeText(jsonText);
      setCopied(true);
      toast.success('JSON copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Copy failed — try selecting and copying manually');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-bg-panel border-l border-bg-border flex flex-col animate-slide-in shadow-modal">
        <div className="flex items-center justify-between px-5 py-4 border-b border-bg-border shrink-0">
          <div>
            <p className="text-2xs font-mono text-txt-muted uppercase tracking-widest mb-0.5">Saved JSON</p>
            <h3 className="font-display font-semibold text-sm text-txt-primary">
              {data?.name || 'Strategy Config'}
            </h3>
          </div>
          <div className="flex items-center gap-2">
            {jsonText && (
              <button onClick={handleCopy} className="btn-ghost flex items-center gap-1.5 text-xs">
                {copied ? <Check size={13} className="text-bull" /> : <Copy size={13} />}
                {copied ? 'Copied' : 'Copy'}
              </button>
            )}
            <button onClick={onClose} className="btn-ghost p-1.5 rounded-lg"><X size={15} /></button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {isLoading && (
            <div className="flex justify-center py-16"><Spinner size={20} /></div>
          )}
          {isError && (
            <ErrorState message="Failed to load strategy JSON" />
          )}
          {!isLoading && !isError && jsonText == null && (
            <EmptyState title="No config data" description="This strategy has no saved JSON payload" />
          )}
          {!isLoading && !isError && jsonText != null && (
            <pre className="font-mono text-xs text-txt-secondary leading-relaxed whitespace-pre-wrap break-all">
              {jsonText}
            </pre>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function AdminUserStrategies() {
  const isSuperAdmin = useSessionStore((s) => s.isSuperAdmin);
  const { data: users, isLoading: usersLoading } = useAdminUsers();
  const [selectedUser, setSelectedUser] = useState(null);
  const [userSearch, setUserSearch]     = useState('');
  const [activeStrategy, setActiveStrategy] = useState(null);
  const [detailsOpen, setDetailsOpen]   = useState(false);
  const [jsonOpen, setJsonOpen]         = useState(false);

  const { data: strategies, isLoading: strategiesLoading, isError: strategiesError } =
    useAdminStrategiesByUser(selectedUser?.id);

  const allUsers = users || [];
  const filteredUsers = userSearch.trim()
    ? allUsers.filter((u) => {
        const q = userSearch.toLowerCase();
        return (
          u.username?.toLowerCase().includes(q) ||
          u.full_name?.toLowerCase().includes(q) ||
          u.email?.toLowerCase().includes(q)
        );
      })
    : allUsers;

  const openDetails = (strategy) => {
    setActiveStrategy(strategy);
    setDetailsOpen(true);
    setJsonOpen(false);
  };

  const openJson = (strategy) => {
    setActiveStrategy(strategy);
    setJsonOpen(true);
    setDetailsOpen(false);
  };

  const closeDrawers = () => {
    setDetailsOpen(false);
    setJsonOpen(false);
  };

  return (
    <div className="max-w-6xl space-y-5">
      <SectionHeader
        title="Admin — User Strategies"
        subtitle="Inspect strategies by user"
      />

      <div className="flex gap-4 min-h-[560px]">
        {/* ── Left: User List ── */}
        <div className="w-64 shrink-0 flex flex-col panel overflow-hidden">
          <div className="px-3 pt-3 pb-2 border-b border-bg-border shrink-0">
            <input
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
              placeholder="Search users…"
              className="input-base text-xs w-full"
            />
          </div>

          <div className="flex-1 overflow-y-auto">
            {usersLoading && (
              <div className="flex justify-center py-10"><Spinner /></div>
            )}
            {!usersLoading && !filteredUsers.length && (
              <p className="text-xs text-txt-muted text-center py-8 font-mono">No users found</p>
            )}
            {!usersLoading && filteredUsers.map((u) => (
              <button
                key={u.id}
                onClick={() => { setSelectedUser(u); closeDrawers(); }}
                className={cn(
                  'w-full text-left px-3 py-2.5 border-b border-bg-border/30 transition-colors relative',
                  selectedUser?.id === u.id
                    ? 'bg-amber/5 text-txt-primary'
                    : 'hover:bg-bg-hover text-txt-secondary'
                )}
              >
                {selectedUser?.id === u.id && (
                  <span className="absolute left-0 inset-y-2 w-0.5 rounded-r-full bg-amber" />
                )}
                <div className="flex items-center justify-between gap-2">
                  <span className="font-semibold text-xs truncate">{u.username}</span>
                  {u.allow_admin_access && <ShieldCheck size={11} className="text-bull shrink-0" title="Admin access enabled" />}
                </div>
                {u.full_name && (
                  <p className="text-2xs text-txt-muted font-mono truncate mt-0.5">{u.full_name}</p>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* ── Right: Strategy List ── */}
        <div className="flex-1 panel overflow-hidden flex flex-col">
          {!selectedUser ? (
            <div className="flex-1 flex items-center justify-center">
              <EmptyState
                title="Select a user"
                description="Choose a user from the left to inspect their strategies"
              />
            </div>
          ) : !isSuperAdmin && !selectedUser.allow_admin_access ? (
            <div className="flex-1 flex items-center justify-center">
              <EmptyState
                title="Admin access not enabled"
                description={`${selectedUser.username} has not enabled admin access. Their strategies are hidden.`}
              />
            </div>
          ) : (
            <>
              <div className="px-4 py-3 border-b border-bg-border shrink-0 flex items-center justify-between">
                <div>
                  <span className="text-xs font-semibold text-txt-primary">{selectedUser.username}</span>
                  {selectedUser.full_name && (
                    <span className="text-xs text-txt-muted font-mono ml-2">— {selectedUser.full_name}</span>
                  )}
                </div>
                {!strategiesLoading && !strategiesError && (
                  <span className="text-2xs font-mono text-txt-muted">
                    {(strategies || []).length} {(strategies || []).length === 1 ? 'strategy' : 'strategies'}
                  </span>
                )}
              </div>

              <div className="flex-1 overflow-y-auto">
                {strategiesLoading && (
                  <div className="flex justify-center py-16"><Spinner size={20} /></div>
                )}
                {strategiesError && (
                  <ErrorState message="Failed to load strategies for this user" />
                )}
                {!strategiesLoading && !strategiesError && !(strategies || []).length && (
                  <EmptyState
                    title="No strategies"
                    description={`${selectedUser.username} has no saved strategies`}
                  />
                )}
                {!strategiesLoading && !strategiesError && (strategies || []).length > 0 && (
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-bg-border">
                        {['ID', 'Name', 'Schema', 'Status', 'Updated', 'Actions'].map((h) => (
                          <th
                            key={h}
                            className="text-left py-3 px-4 text-2xs font-mono text-txt-muted uppercase tracking-widest"
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {(strategies || []).map((s) => (
                        <tr
                          key={s.id}
                          className={cn(
                            'border-b border-bg-border/30 hover:bg-bg-hover transition-colors',
                            activeStrategy?.id === s.id && (detailsOpen || jsonOpen) && 'bg-amber/5'
                          )}
                        >
                          <td className="py-3 px-4 font-mono text-txt-muted">{s.id}</td>
                          <td className="py-3 px-4 font-semibold text-txt-primary max-w-[180px] truncate">
                            {s.name}
                          </td>
                          <td className="py-3 px-4">
                            <SchemaBadge version={s.schema_version} />
                          </td>
                          <td className="py-3 px-4">
                            <StatusChip status={s.status} />
                          </td>
                          <td className="py-3 px-4 font-mono text-txt-muted">
                            {fmtDateTime(s.updated_at || s.created_at)}
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-1.5">
                              <button
                                onClick={() => openDetails(s)}
                                className="btn-ghost flex items-center gap-1 text-2xs py-1 px-2"
                                title="View Details"
                              >
                                <Eye size={11} /> Details
                              </button>
                              <button
                                onClick={() => openJson(s)}
                                className="btn-ghost flex items-center gap-1 text-2xs py-1 px-2"
                                title="View JSON"
                              >
                                <Code size={11} /> JSON
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      <StrategyDetailsDrawer
        strategy={activeStrategy}
        user={selectedUser}
        open={detailsOpen}
        onClose={closeDrawers}
      />
      <StrategyJsonDrawer
        strategyId={activeStrategy?.id}
        open={jsonOpen}
        onClose={closeDrawers}
      />
    </div>
  );
}
